import { Injectable } from '@angular/core';
import { LocationService } from './location.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { RefreshService } from './refresh.service';
import { finalize } from 'rxjs';
import { SignalrService } from './signalr.service';
import { RefreshTokenCommand, TokensClient } from '../model/fss.api';
import {
  AUTH_REFRESH_TOKEN_STORAGE_KEY,
  AUTH_TOKEN_STORAGE_KEY,
} from '../constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _tokenName = AUTH_TOKEN_STORAGE_KEY;
  private readonly _refreshTokenName = AUTH_REFRESH_TOKEN_STORAGE_KEY;

  constructor(
    private _jwtHelper: JwtHelperService,
    private _locationService: LocationService,
    private _refreshService: RefreshService,
    private _tokensClient: TokensClient,
    private signalRService: SignalrService,
  ) {}

  get getUserClaims(): string[] {
    return this.getDecodedToken?.Permission;
  }

  get getDecodedToken(): any {
    if (!this.getToken) {
      return null;
    }
    return this._jwtHelper.decodeToken(this.getToken);
  }

  get getToken(): string | null {
    return localStorage.getItem(this._tokenName);
  }

  get getRefreshToken(): string | null {
    return localStorage.getItem(this._refreshTokenName);
  }

  public isUserAuthenticated = (): boolean => {
    if (!this.getToken) {
      return false;
    }

    return !this.isTokenExpired;
  };

  get isTokenExpired(): boolean {
    if (!this.getToken) {
      return true;
    }
    return this._jwtHelper.isTokenExpired(this.getToken);
  }

  public logout() {
    this.removeTokens();
    this._locationService.routeToLogin();
  }

  setTokens(token: string, refreshToken: string) {
    localStorage.setItem(this._tokenName, token);
    localStorage.setItem(this._refreshTokenName, refreshToken);
  }

  removeTokens() {
    localStorage.removeItem(this._tokenName);
    localStorage.removeItem(this._refreshTokenName);
    this._refreshService.reset();
    this.signalRService.closeConnection();
  }

  /**
   * start signalR connection
   */
  public async startSignalR() {
    // Wait for the old connection to fully stop before starting a new one —
    // otherwise both can briefly be alive at once and each deliver the same push.
    await this.signalRService.closeConnection();
    await this.signalRService.startConnection();
  }

  // This method can be called a couple of different ways
  // *hasClaim="'claimType'"  // Assumes claimValue is true
  // *hasClaim="'claimType:value'" // Compares claimValue to value
  // *hasClaim="['claimType1','claimType2:value','claimType3']"
  hasClaim(claimValue: string | string[]): boolean {
    if (!this.isUserAuthenticated()) {
      return false;
    }

    let userHasClaim = false;

    // See if an array of values was passed in.
    if (typeof claimValue === 'string') {
      userHasClaim = this.isClaimValid(claimValue);
    } else {
      const claims: string[] = claimValue;
      if (claims) {
        for (let index = 0; index < claims.length; index++) {
          userHasClaim = this.isClaimValid(claims[index]);
          // If one is successful, then let them in
          if (userHasClaim) {
            break;
          }
        }
      }
    }

    return userHasClaim;
  }

  private isClaimValid(claimValue: string): boolean {
    let ret = false;
    const regEx = new RegExp(`\\b${claimValue}\\b`);

    for (let i = 0; i < this.getUserClaims?.length; ++i) {
      if (regEx.test(this.getUserClaims[i])) {
        ret = true;
        break;
      }
    }
    return ret;
  }

  // Method needed for manually refreshing(so we can get updated profile picture from JWT)
  refreshToken() {
    this._refreshService.startRefreshing();

    this._tokensClient
      .refresh(
        new RefreshTokenCommand({
          refreshToken: this.getRefreshToken!,
          token: this.getToken!,
        }),
      )
      .pipe(
        finalize(() => {
          this._refreshService.currentlyRefreshing = false;
          this._refreshService.setTokenRefreshed = this.getToken!;
        }),
      )
      .subscribe((res) => {
        this.setTokens(res.result.value!.token!, res.result.value!.refreshToken!);
      });
  }
}
